/**
 * A IOS-styed switch using MUi.
 * The base code comes from the examples of the documentation of MUI.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2025
 */

import { styled } from '@mui/material/styles'
import Switch, { SwitchProps } from '@mui/material/Switch'
import { ReactElement } from 'react'
import './styles/IosSwitch.css'

const DEFAULT_BACKGROUND_COLOR_OFF = '#737373'
const DEFAULT_BACKGROUND_COLOR_ON = '#F67D69'

interface IosSwitchProps extends SwitchProps {
  id?: string
  height?: number
  width?: number
  label?: ReactElement | string
  backgroundColor?: string
}

const IosSwitch = styled(
  // Do not pass the props which don't exist in SwitchProps to Switch,
  // else a warning of unsupported props will be raised.
  // We must destructure the props. But because the additional props
  // are not used in the return function, but in the styling,
  // we must disable the lint rule about unused variables.

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ({ id, height, width, label, backgroundColor, ...rest }: IosSwitchProps) => {
    return (
      <div className="ios-switch" id={id}>
        <Switch
          focusVisibleClassName=".Mui-focusVisible"
          disableRipple
          {...rest}
        />
        {label}
      </div>
    )
  }
)<IosSwitchProps>(({
  theme,
  height = 26,
  width = 42,
  backgroundColor = DEFAULT_BACKGROUND_COLOR_OFF,
}) => {
  // Compute the size and the amplitude of movement of the thumb in the switch
  const compThumbMargin = 2
  const thumbSize = height - 2 * compThumbMargin
  const trackBorderRadius = height / 2
  const thumbMove = width - (2 * compThumbMargin + thumbSize)

  return {
    width: width,
    height: height,
    padding: 0,
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: 2,
      transitionDuration: '300ms',
      '&.Mui-checked': {
        transform: `translateX(${thumbMove}px)`,
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: DEFAULT_BACKGROUND_COLOR_ON,
          opacity: 1,
          border: 0,
          ...theme.applyStyles('dark', {
            backgroundColor: DEFAULT_BACKGROUND_COLOR_ON,
          }),
        },
        '&.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.5,
        },
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': {
        color: DEFAULT_BACKGROUND_COLOR_ON,
        border: '6px solid #fff',
      },
      '&.Mui-disabled .MuiSwitch-thumb': {
        color: theme.palette.grey[100],
        ...theme.applyStyles('dark', {
          color: theme.palette.grey[600],
        }),
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.7,
        ...theme.applyStyles('dark', {
          opacity: 0.3,
        }),
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: thumbSize,
      height: thumbSize,
    },
    '& .MuiSwitch-track': {
      borderRadius: trackBorderRadius,
      backgroundColor: backgroundColor,
      opacity: 1,
      transition: theme.transitions.create(['background-color'], {
        duration: 500,
      }),
      ...theme.applyStyles('dark', {
        backgroundColor: '#39393D',
      }),
    },
  }
})

export default IosSwitch
